<template>
  <main class="container">
    <h1 class="page-title" data-testid="tenancy-date-form-title">
      Tell Us About Your Tenancy
    </h1>

    <BackButton class="back-link" />

    <InfoBox v-if="getTenancyLengthAlert" class="infobox">
      <template v-slot>
        <p>
          Are you looking for a contract shorter than 10 months? Call our
          helpful agents for a quote on
          <b><a href="tel:03300539350">0330 053 9350</a></b> (9am-5pm every
          weekday).
        </p>
      </template>
    </InfoBox>

    <form
      class="form tenancy-form"
      novalidate
      @submit.prevent="formSubmit"
      autocomplete="on"
    >
      <fieldset class="form__fieldset">
        <div class="tenancy-form__wrapper">
          <label class="form__label" for="startDate">
            <span class="form__label-text">Tenancy start date</span>
          </label>
          <div>
            <FormError
              v-if="startDateErrorMessage"
              :msg="startDateErrorMessage"
              data-testid="tenancy-start-date-error"
            />
          </div>
          <div class="form__date-input">
            <input
              class="form__input"
              :class="{ '--has-error': $v.startDate.$error }"
              type="date"
              id="startDate"
              data-testid="start-date"
              name="startDate"
              :min="minStartDate | FormatDate"
              :max="maxStartDate | FormatDate"
              required
              v-model.trim="startDate"
              @change="adjustEndDate"
              @click="showDatePicker"
              ref="startDateField"
            />
            <CalendarIcon class="form__date-icon" height="30px" />
          </div>

          <label class="form__label" for="endDate">
            <span class="form__label-text">Tenancy end date</span>
          </label>
          <div>
            <FormError
              v-if="$v.endDate.$error && !$v.endDate.required"
              msg="Tenancy End date required"
              data-testid="end-date-required-error"
            />
            <FormError
              v-if="
                $v.endDate.$error && $v.endDate.required && !$v.endDate.minValue
              "
              :msg="`Minimum term is ${minTenancyLengthMonths} months`"
              data-testid="end-date-min-term-error"
            />
            <FormError
              v-if="
                $v.endDate.$error && $v.endDate.required && !$v.endDate.maxValue
              "
              msg="Tenancy End date cannot be in the far future"
              data-testid="end-date-far-future-error"
            />
          </div>
          <div class="form__date-input">
            <input
              class="form__input"
              :class="{ '--has-error': $v.endDate.$error }"
              type="date"
              id="endDate"
              data-testid="end-date"
              name="endDate"
              :min="minEndDate | FormatDate"
              :max="maxEndDate | FormatDate"
              required
              v-model.trim="endDate"
              @click="showDatePicker"
              ref="endDateField"
            />
            <CalendarIcon class="form__date-icon" height="30px" />
          </div>
        </div>
      </fieldset>

      <button
        class="btn btn--big form__submit"
        type="submit"
        @click="trackSubmitBtn"
        data-testid="next-button"
      >
        Next
      </button>
    </form>
  </main>
</template>

<script>
import Pikaday from "pikaday";
import { required } from "vuelidate/lib/validators";
import date from "date-and-time";
import FormError from "@/components/forms/FormError.vue";
import CalendarIcon from "@/assets/svg/icon-calendar.svg";
import { mapGetters } from "vuex";
import InfoBox from "@/components/InfoBox.vue";

export default {
  name: "TenancyLength",
  data() {
    return {
      startDatePicker: undefined,
      endDatePicker: undefined,
    };
  },
  computed: {
    ...mapGetters({
      isTenancyStartDateDynamic: "isTenancyStartDateDynamic",
      getTenancyLengthAlert: "getTenancyLengthAlert",
    }),
    startDate: {
      get() {
        return this.$store.state.houseInformation.startDate;
      },
      set(value) {
        this.$store.commit("SET_TENANCY_START_DATE", value);
      },
    },
    startDateErrorMessage() {
      const startDate = this.$v.startDate;

      switch (true) {
        case startDate.$error && !startDate.required:
          return "Tenancy Start date required";
        case startDate.$error && startDate.required && !startDate.maxValue:
          return "Tenancy Start date cannot be in the far future";
        case startDate.required && !this.isStartAfterTenancyMin:
          return (
            "Please choose a start date on or after " +
            date.format(this.minStartDate, "DD/MM/YYYY", false)
          );
        default:
          return "";
      }
    },
    endDate: {
      get() {
        return this.$store.state.houseInformation.endDate;
      },
      set(value) {
        this.$store.commit("SET_TENANCY_END_DATE", value);
      },
    },
    hasBroadband() {
      return this.$store.getters.hasBroadband;
    },
    minTenancyLengthMonths() {
      return this.$store.state.uiState.minTenancyLengthMonths;
    },
    isStartAfterTenancyMin() {
      const startDateEndOfDay = new Date(this.startDate).setHours(23, 59, 44);

      return startDateEndOfDay >= this.minStartDate;
    },
    minStartDate() {
      const today = new Date(new Date().setHours(0, 0, 0));

      if (
        // current date is between 1 July and 30 September
        this.isTenancyStartDateDynamic &&
        today >= new Date(today.getFullYear(), 6, 1).setHours(0, 0, 0) &&
        today <= new Date(today.getFullYear(), 8, 30).setHours(23, 59, 59)
      ) {
        //  user can still pick dates on/after 1 July
        return new Date(today.getFullYear(), 6, 1);
      }

      return today;
    },
    maxStartDate() {
      // Can only be in the current year
      const maxDate = new Date();
      maxDate.setFullYear(this.minStartDate.getFullYear() + 1, 11, 31);
      maxDate.setHours(23, 59, 59);
      return maxDate;
    },
    minEndDate() {
      let futureDate = this.startDate ? new Date(this.startDate) : new Date();
      futureDate.setHours(23, 59, 59);
      futureDate = date.addMonths(futureDate, this.minTenancyLengthMonths);
      futureDate = date.addDays(futureDate, -1);
      return futureDate;
    },
    maxEndDate() {
      let futureDate = this.startDate ? new Date(this.startDate) : new Date();
      return date.addDays(futureDate, 465); //maximum contract duration of 465 days
    },
  },
  filters: {
    FormatDate(dateValue) {
      return date.format(dateValue, "YYYY-MM-DD", true);
    },
  },
  watch: {
    minEndDate() {
      if (!this.checkDateInput()) {
        this.endDatePicker.gotoDate(this.minEndDate);
        this.endDatePicker.setMinDate(this.minEndDate);
      }
    },
    maxEndDate() {
      if (!this.checkDateInput()) {
        this.endDatePicker.setMaxDate(this.maxEndDate);
      }
    },
  },
  methods: {
    formatDate(dateValue, utc = true) {
      return date.format(dateValue, "YYYY-MM-DD", utc);
    },
    selectTodaysDate() {
      let endDate = this.startDate ? new Date(this.startDate) : new Date();
      endDate.setHours(23, 59, 59);
      endDate = date.addYears(endDate, 1);
      endDate = date.addDays(endDate, -1);

      if (!this.checkDateInput()) {
        this.startDatePicker.setDate(new Date());
        this.endDatePicker.setDate(endDate);
      }

      this.$store.commit("SET_TENANCY_START_DATE", this.formatDate(new Date()));
      this.$store.commit("SET_TENANCY_END_DATE", this.formatDate(endDate));
    },
    formSubmit() {
      this.$v.$touch();

      if (!this.$v.$invalid && this.isStartAfterTenancyMin) {
        if (this.hasBroadband) {
          this.$router.push("/broadband-date");
        } else {
          this.$router.push("/full-summary");
        }
      }
    },
    adjustEndDate(event) {
      // when start date changes, automatically set end date 1 year later
      let endDate = new Date(event.target.value);
      endDate.setHours(23, 59, 59);
      endDate = date.addYears(endDate, 1);
      endDate = date.addDays(endDate, -1);

      this.$store.commit("SET_TENANCY_END_DATE", this.formatDate(endDate));
    },
    trackSubmitBtn() {
      this.trackGoogleEvent(
        "Click Next Tenancy",
        "Create Account",
        "Confirm tenancy details",
      );
      this.trackFacebookEvent("Click Next Tenancy");
    },
    showDatePicker(event) {
      if (event) {
        event.target.showPicker();
      }
    },
  },
  mounted() {
    if (!this.checkDateInput()) {
      this.startDatePicker = new Pikaday({
        field: this.$refs.startDateField,
        minDate: this.minStartDate,
        maxDate: this.maxStartDate,
      });
      this.endDatePicker = new Pikaday({
        field: this.$refs.endDateField,
        minDate: this.minEndDate,
        maxDate: this.maxEndDate,
      });
    }
  },
  destroyed() {
    if (!this.checkDateInput()) {
      this.startDatePicker.destroy();
      this.endDatePicker.destroy();
    }
  },
  validations() {
    return {
      startDate: {
        required,
        minValue: (value) => {
          const isSameDay = this.startDate
            ? date.isSameDay(new Date(this.startDate), this.minStartDate)
            : false;

          let validDate = new Date(value);
          validDate.setHours(23, 59, 59);

          return isSameDay || validDate >= this.minStartDate;
        },
        maxValue: (value) => {
          return new Date(value) <= this.maxStartDate;
        },
      },
      endDate: {
        required,
        minValue: (value) => {
          let validDate = new Date(value);
          validDate.setHours(23, 59, 59);
          return validDate >= this.minEndDate;
        },
        maxValue: (value) => {
          let validDate = new Date(value);
          validDate.setHours(23, 59, 59);
          return validDate <= this.maxEndDate;
        },
      },
    };
  },
  components: {
    InfoBox,
    FormError,
    CalendarIcon,
  },
};
</script>

<style lang="scss" scoped>
.tenancy-form__wrapper {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(3, auto);
  grid-auto-flow: column;

  @include media("<medium") {
    grid-template-columns: 1fr;
    grid-template-rows: repeat(6, auto);
  }
}
</style>
